// ============ MAIN APP SHELL ============
import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { AuroraBG, Sidebar, TopBar, TickerTape } from './components/core';
import { LandingPage, PremiumGate, MaintenanceGate, UpgradeModal, BottomNav } from './components/landing';
import { useTweaks, TweaksPanel, TweakSection, TweakToggle, TweakSlider, TweakColor } from './components/tweaks';
import { DashboardPage, ScannerPage, IpoPage, EarningsPage, DividendsPage } from './pages/pages';
import { HumanDesignPage, AllocationPage, AuthModal } from './pages/pages2';
import { TopTools, OnboardingModal, SettingsPage } from './pages/features';
// Code-split the heavier, non-entry pages — each is a page-only module, so the
// initial load ships just the shell + dashboard and every other page's JS is
// fetched on first visit (see the <Suspense> around the page outlet below).
const AnalysisPage = lazy(() => import('./pages/analysis').then(m => ({ default: m.AnalysisPage })));
const NewsDailyPage = lazy(() => import('./pages/newsdaily').then(m => ({ default: m.NewsDailyPage })));
const CoursesPage = lazy(() => import('./pages/courses').then(m => ({ default: m.CoursesPage })));
const TrackPortPage = lazy(() => import('./pages/trackport').then(m => ({ default: m.TrackPortPage })));
const LegendsPage = lazy(() => import('./pages/legends').then(m => ({ default: m.LegendsPage })));
const AdminPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminPage })));
const HelpPage = lazy(() => import('./pages/help').then(m => ({ default: m.HelpPage })));
import { MM_LIVE } from './live';
import { ASSETS } from './data';
import { payStatus, fetchSession, fetchState, saveState, authLogout } from './services/api';
import { mmIsPremium } from './lib/premium';
import type { MmUser } from './lib/premium';
import { useLang, useT } from './lib/i18n';

// Guards the lazy-loaded page outlet: if a code-split chunk fails to fetch
// (network blip, stale deploy) the page shows a retry instead of a blank screen.
// Keyed by page in the tree so navigating away clears a caught error.
class PageErrorBoundary extends React.Component<{ children: React.ReactNode; fallback: React.ReactNode }, { err: boolean }> {
  state = { err: false };
  static getDerivedStateFromError() { return { err: true }; }
  // Without this the page swaps to the fallback and the reason is gone: the
  // reader reports "it says it failed to load" and there is nothing to look at.
  // React does not log an error a boundary has handled.
  componentDidCatch(err: unknown, info: unknown) { console.error('[page] render failed:', err, info); }
  render() { return this.state.err ? this.props.fallback : this.props.children; }
}

const TWEAK_DEFAULTS = {
  fxIntensity: 1,
  accent: ['#00e0b0', '#ff3b5c', '#8b5cf6'],
  glass: true,
  fontScale: 1,
  serifHeads: true,
  livePrices: true,
};

const TITLES: Record<string, [string, string]> = {
  dashboard: ['ภาพรวมตลาด · ทุก Asset', 'Market Overview · All Assets'],
  analysis: ['วิเคราะห์รายตัว · Fundamental + Technical', 'Analysis · Fundamental + Technical'],
  scanner: ['Scanner · ตัวกรองอัจฉริยะ', 'Scanner · Smart Filters'],
  ipo: ['IPO · หุ้นเข้าใหม่', 'IPO · New Listings'],
  earnings: ['Earnings · ปฏิทินงบการเงิน', 'Earnings · Calendar'],
  dividends: ['Dividends · ปฏิทินปันผล', 'Dividends · Calendar'],
  allocation: ['Asset Allocation · จัดพอร์ต', 'Asset Allocation'],
  humandesign: ['Human Design · ถอดรหัสนักลงทุน', 'Human Design · Investor Decoder'],
  courses: ['Academy · คอร์สเรียนการลงทุน', 'Academy · Investing Courses'],
  dailynews: ['Daily News · วิเคราะห์ข่าวรายวัน', 'Daily News · Market Analysis'],
  trackport: ['Track Port · ติดตามพอร์ตการลงทุน', 'Track Port · Portfolio Tracking'],
  settings: ['Settings · ตั้งค่าและโปรไฟล์', 'Settings · Profile'],
  help: ['Help · คู่มือการใช้งาน', 'Help · User Guide'],
  admin: ['Admin · สถานะระบบ', 'Admin · System Status'],
  legends: ['Investor Legends · เกมเรียนรู้', 'Investor Legends · Learn & Play'],
};

// Every value `page` is allowed to hold. Keep in step with PAGES below.
//
// This exists because `page` was reachable from outside: TopBar wired its
// log-out button straight to doLogout, so React handed the click event in as
// the destination and setPage stored the event object. The title then rendered
// it as a child ("Objects are not valid as a React child") and took the whole
// app down - and the effect below had already written "[object Object]" into
// localStorage, so the next load came up on a page that does not exist and sat
// blank until the reader clicked a menu item themselves.
const PAGE_KEYS = new Set([
  'landing', 'dashboard', 'analysis', 'scanner', 'ipo', 'earnings', 'dividends',
  'allocation', 'humandesign', 'courses', 'dailynews', 'trackport', 'settings',
  'help', 'admin', 'legends',
]);
const safePage = (p: unknown): string => (typeof p === 'string' && PAGE_KEYS.has(p) ? p : 'landing');

export default function App() {
  const { lang } = useLang();
  const tr = useT();
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  // Validated on read, not just on write: browsers that already stored the
  // broken value keep it until something rejects it, and a stale "[object
  // Object]" would otherwise strand that reader on an empty screen forever.
  const [page, setPage] = useState<string>(() => safePage(localStorage.getItem('mm_page')));
  const [sym, setSym] = useState<string>(() => localStorage.getItem('mm_sym') || 'NVDA');
  const [user, setUser] = useState<MmUser | null>(() => {
    try { return JSON.parse(localStorage.getItem('mm_user') || 'null'); } catch { return null; }
  });
  const [authMode, setAuthMode] = useState<string | null>(null);
  const [pageKey, setPageKey] = useState(0);
  const [watchlist, setWatchlist] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('mm_watch') || 'null') || []; } catch { return []; }
  });
  const [alerts, setAlerts] = useState<any[]>(() => {
    try { return JSON.parse(localStorage.getItem('mm_alerts') || 'null') || []; } catch { return []; }
  });
  const [showOnboard, setShowOnboard] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [payOk, setPayOk] = useState(false);
  const [, setLiveTick] = useState(0);
  // Server-account availability (DB present) + which social logins are configured,
  // from /api/auth/session. A button for an unconfigured provider is worse than
  // no button: it sends the reader to a 503 they cannot do anything about.
  const [authCfg, setAuthCfg] = useState<{ accounts: boolean; line: boolean; google: boolean; stripe: boolean }>({ accounts: false, line: false, google: false, stripe: false });
  // True once we've hydrated state from the backend for a logged-in user — gates
  // the sync-back effect so we never overwrite server state with empty local state.
  const synced = useRef(false);
  const backed = !!(user && user.id && authCfg.accounts); // logged in against the server

  // Restore a server session on load (and after a LINE redirect), then hydrate
  // the user's watchlist/alerts from the backend. Falls back silently to the
  // localStorage-seeded state when there is no DB / no session.
  useEffect(() => {
    let alive = true;
    fetchSession().then(info => {
      if (!alive) return;
      setAuthCfg({ accounts: info.accounts, line: info.line, google: !!info.google, stripe: !!info.stripe });
      if (!info.user && info.accounts) {
        // Server-side accounts are live but this browser has no valid session —
        // drop any cached user so stale (e.g. ex-admin) state can't linger.
        setUser(null);
      }
      if (info.user) {
        setUser(info.user);
        // Only a successful read marks the lists as synced. A failed one used
        // to come back as empty lists, and the sync effect below then saved
        // those over the member's real watchlist and alerts. Until a read
        // succeeds nothing is written; a few retries cover a server restart.
        const hydrate = (attempt: number) => {
          fetchState().then(st => {
            if (!alive) return;
            setWatchlist(st.watch);
            setAlerts(st.alerts);
            if (st.onboarded) localStorage.setItem('mm_onboarded', '1');
            synced.current = true;
          }).catch(() => {
            if (alive && attempt < 5) setTimeout(() => hydrate(attempt + 1), [2000, 5000, 15000, 30000, 60000][attempt]);
          });
        };
        hydrate(0);
      }
      // surface the social-login redirect result
      const p = new URLSearchParams(window.location.search);
      const back = p.get('login');
      if (back === 'line' || back === 'google' || p.get('login_error')) {
        window.history.replaceState(window.history.state, '', window.location.pathname);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Plans change on the server - an approved slip, a trial running out, a grant
  // from the back office - but the session was read only once, at startup. A
  // customer who sent a slip, closed the waiting screen and came back after the
  // approval stayed locked out until they reloaded. Coming back to the tab now
  // re-reads it (at most once a minute).
  useEffect(() => {
    let last = Date.now();
    const onBack = () => {
      if (document.visibilityState === 'hidden' || Date.now() - last < 60000) return;
      last = Date.now();
      fetchSession().then(info => {
        if (info.user) {
          const nu = info.user;
          setUser(u => (u && u.id === nu.id && u.plan === nu.plan && u.tier === nu.tier && u.trialEnd === nu.trialEnd) ? u : nu);
        } else if (info.accounts) {
          setUser(u => (u ? null : u));   // the session ended (expired, or signed out elsewhere)
        }
      }).catch(() => {});
    };
    window.addEventListener('focus', onBack);
    document.addEventListener('visibilitychange', onBack);
    return () => { window.removeEventListener('focus', onBack); document.removeEventListener('visibilitychange', onBack); };
  }, []);

  // Debounced push of state changes to the backend for logged-in users.
  useEffect(() => {
    if (!backed || !synced.current) return;
    const h = setTimeout(() => {
      saveState({ watch: watchlist, alerts, onboarded: localStorage.getItem('mm_onboarded') === '1' }).catch(() => {});
    }, 600);
    return () => clearTimeout(h);
  }, [backed, watchlist, alerts]);

  // Handle return from Stripe Checkout (?pay_session=... / ?pay_cancel=1)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clean = () => window.history.replaceState(window.history.state, '', window.location.pathname);
    if (params.get('pay_cancel')) { clean(); return; }
    const sid = params.get('pay_session');
    if (!sid) return;
    payStatus(sid).then(s => {
      if (s.paid) {
        setUser(u => u ? { ...u, plan: 'premium', tier: s.tier || 'Premium', billing: s.billing, trialEnd: s.recurring ? null : Date.now() + (s.days || 365) * 864e5 } : u);
        setPayOk(true);
        setShowUpgrade(false);
        // For logged-in users the backend is now the source of truth — pull the
        // authoritative plan (handles recurring/no-expiry correctly).
        fetchSession().then(info => { if (info.user) setUser(info.user); }).catch(() => {});
      }
      clean();
    });
  }, []);

  useEffect(() => {
    if (!t.livePrices) { MM_LIVE.stop(); return; }
    MM_LIVE.start();
    const h = () => setLiveTick(x => x + 1);
    window.addEventListener('mm-live-tick', h);
    return () => { window.removeEventListener('mm-live-tick', h); MM_LIVE.stop(); };
  }, [t.livePrices]);

  useEffect(() => { localStorage.setItem('mm_page', page); }, [page]);
  useEffect(() => { localStorage.setItem('mm_sym', sym); }, [sym]);
  useEffect(() => { localStorage.setItem('mm_watch', JSON.stringify(watchlist)); }, [watchlist]);
  useEffect(() => { localStorage.setItem('mm_alerts', JSON.stringify(alerts)); }, [alerts]);
  useEffect(() => {
    if (user) localStorage.setItem('mm_user', JSON.stringify(user));
    else localStorage.removeItem('mm_user');
  }, [user]);

  useEffect(() => {
    const r = document.documentElement.style;
    r.setProperty('--up', t.accent[0]);
    r.setProperty('--down', t.accent[1]);
    r.setProperty('--violet', t.accent[2]);
    r.setProperty('--font-scale', String(t.fontScale));
    document.body.classList.toggle('no-glass', !t.glass);
    document.body.classList.toggle('sans-heads', !t.serifHeads);
  }, [t]);

  // safePage here as well as at startup: this is the only door into setPage,
  // so one guard on it means no caller can put a non-page into the state no
  // matter what they pass - including a click event from an onClick={nav}.
  // Pages are React state, so the browser never knew the reader had moved: the
  // phone's Back button left the site from any page. Each move now leaves a
  // history entry (state only - the address stays as it is) and Back walks them.
  const nav = (p: string) => {
    const next = safePage(p);
    setPage(next);
    setPageKey(k => k + 1);
    try { if (window.history.state?.mmPage !== next) window.history.pushState({ ...(window.history.state || {}), mmPage: next }, ''); } catch { /* history unavailable */ }
  };
  useEffect(() => {
    try { window.history.replaceState({ ...(window.history.state || {}), mmPage: page }, ''); } catch { /* */ }
    const onPop = (e: PopStateEvent) => {
      const p = e.state?.mmPage;
      if (typeof p !== 'string') return;
      setPage(safePage(p));
      setPageKey(k => k + 1);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // `to` is defaulted AND validated: a default only fires on undefined, and the
  // way this broke was a click event being passed - which is defined, so the
  // default never applied.
  const doLogout = (to: unknown = 'landing') => {
    const dest = typeof to === 'string' ? to : 'landing';
    authLogout().catch(() => {});
    synced.current = false;
    setUser(null);
    setWatchlist([]);
    setAlerts([]);
    nav(dest);
  };
  const pick = (s: string) => {
    if (ASSETS.some(a => a.sym === s)) { setSym(s); nav('analysis'); }
  };
  const isPrem = () => mmIsPremium(user);
  // Which plan the modal should open on. 'm' = Gold monthly, 'y' = Premium yearly.
  const [upgradeBill, setUpgradeBill] = useState<'m' | 'y'>('m');
  // The argument is validated rather than trusted: most call sites pass this
  // straight to onClick, which hands in a React event. Reading that as a plan is
  // how the log-out button once passed a click event in as a page name and took
  // the whole app down.
  const askUpgrade = (bill?: unknown) => {
    setUpgradeBill(bill === 'y' ? 'y' : 'm');
    if (user) setShowUpgrade(true); else setAuthMode('signup');
  };
  const toggleWatch = (s: string) => {
    if (!user) { setAuthMode('signup'); return; }
    if (!watchlist.includes(s) && watchlist.length >= 10 && !isPrem()) { askUpgrade(); return; }
    setWatchlist(w => w.includes(s) ? w.filter(x => x !== s) : [...w, s]);
  };
  const tools = {
    watchlist, toggleWatch, alerts, user, isPremium: mmIsPremium(user),
    onAuth: (m: string) => setAuthMode(m),
    addAlert: (al: any) => {
      if (!user) { setAuthMode('signup'); return; }
      if (alerts.length >= 5 && !isPrem()) { askUpgrade(); return; }
      setAlerts(a => [...a, al]);
    },
    removeAlert: (id: any) => setAlerts(a => a.filter(x => x.id !== id)),
    onPick: pick, openSettings: () => nav('settings'), openHelp: () => nav('help'),
  };

  const PAGES: Record<string, React.ReactNode> = {
    dashboard: <DashboardPage onPick={pick} onNav={nav} user={user} onAuth={(m: string) => setAuthMode(m)} onUpgrade={() => setShowUpgrade(true)} />,
    analysis: <AnalysisPage sym={sym} onPick={pick} watchlist={watchlist} toggleWatch={toggleWatch} user={user} onUpgrade={() => setShowUpgrade(true)} onAuth={(m: string) => setAuthMode(m)} />,
    scanner: <ScannerPage onPick={pick} user={user} onUpgrade={() => setShowUpgrade(true)} onAuth={(m: string) => setAuthMode(m)} />,
    ipo: <IpoPage onPick={pick} />,
    earnings: <EarningsPage onPick={pick} user={user} onUpgrade={() => setShowUpgrade(true)} onAuth={(m: string) => setAuthMode(m)} />,
    dividends: <DividendsPage onPick={pick} />,
    allocation: <PremiumGate user={user} onUpgrade={() => setShowUpgrade(true)} onAuth={(m: string) => setAuthMode(m)} title={tr('Asset Allocation · จัดพอร์ต', 'Asset Allocation')} desc={tr('พอร์ตแนะนำตาม Human Design Type + ระดับความเสี่ยง พร้อมแบ่งเงินลงทุนรายสินทรัพย์ — สำหรับสมาชิก Gold/Premium', 'A portfolio tailored to your Human Design type + risk level, with per-asset allocation — for Gold/Premium members')}><AllocationPage user={user} onPick={pick} /></PremiumGate>,
    humandesign: <PremiumGate user={user} onUpgrade={() => setShowUpgrade(true)} onAuth={(m: string) => setAuthMode(m)} title={tr('Human Design · ถอดรหัสนักลงทุน', 'Human Design · Investor Decoder')} desc={tr('เปิดเผย Investor Bodygraph, สไตล์นักลงทุนตามพลังงาน และชาร์ตฉบับเต็ม — สำหรับสมาชิก Gold/Premium', 'Reveals your Investor Bodygraph, energy-based investing style and full chart — for Gold/Premium members')}><HumanDesignPage user={user} setUser={setUser} onNav={nav} onUpgrade={() => setShowUpgrade(true)} onAuth={(m: string) => setAuthMode(m)} /></PremiumGate>,
    // The Academy catalogue is open. Only the 19-class Technical programme is
    // still gated, and that gate lives inside the page (courses.tsx) rather than
    // here — locking the whole page would take the catalogue down with it.
    courses: <CoursesPage user={user} onAuth={(m: string) => setAuthMode(m)} />,
    dailynews: <NewsDailyPage onPick={pick} user={user} onUpgrade={() => setShowUpgrade(true)} onAuth={(m: string) => setAuthMode(m)} />,
    // The EA store sells per-build, exactly like the Academy — so the page is
    // browsable by everyone; downloads are gated per purchase on the server.
    // Closed again at the owner's request. It was briefly opened behind
    // PremiumGate because the Gold card advertises it; while it is shut, the
    // thing to watch is that the price list is still promising it - see the
    // note beside the Gold plan features.
    trackport: <MaintenanceGate user={user} onNav={nav} title={tr('ติดตามพอร์ต · Track Port', 'Track Port')}><TrackPortPage user={user} onAuth={(m: string) => setAuthMode(m)} /></MaintenanceGate>,
    settings: <SettingsPage user={user} setUser={setUser} onAuth={(m: string) => setAuthMode(m)} onLogout={() => doLogout('dailynews')} onNav={nav} />,
    help: <HelpPage onNav={nav} />,
    admin: <AdminPage user={user} />,
    legends: <LegendsPage />,
  };

  return (
    <div className="mm-app">
      <AuroraBG intensity={t.fxIntensity} />
      {page === 'landing' ? (
        <LandingPage onAuth={(m: string) => setAuthMode(m)} onEnter={() => nav('dashboard')}
          user={user} onUpgrade={askUpgrade} />
      ) : (
        <React.Fragment>
          <Sidebar page={page} onNav={nav} user={user} />
          <div className="mm-main">
            <TickerTape />
            <TopBar title={(TITLES[page] || [page, page])[lang === 'en' ? 1 : 0]} user={user} onNav={nav} onPick={pick}
              topTools={<TopTools {...tools} />}
              onAuth={(m: string) => setAuthMode(m)} onLogout={() => doLogout()} onUpgrade={askUpgrade} />
            <main className="mm-content" key={pageKey}>
              <PageErrorBoundary key={page} fallback={
                <div className="mm-page-loading">
                  {tr('โหลดหน้านี้ไม่สำเร็จ', 'This page failed to load')} —{' '}
                  <button className="mm-btn ghost sm" onClick={() => window.location.reload()}>{tr('ลองใหม่', 'Retry')}</button>
                </div>
              }>
                <Suspense fallback={<div className="mm-page-loading dim">{tr('กำลังโหลด…', 'Loading…')}</div>}>
                  {PAGES[page]}
                </Suspense>
              </PageErrorBoundary>
            </main>
          </div>
          <BottomNav page={page} onNav={nav} user={user} />
        </React.Fragment>
      )}
      {authMode && (
        <AuthModal mode={authMode} lineEnabled={authCfg.line} googleEnabled={authCfg.google} onClose={() => setAuthMode(null)}
          onLogin={(u: any, isSignup?: boolean) => {
            setUser(u);
            setAuthMode(null);
            // Server-backed account: hydrate its saved state, then allow sync-back.
            if (u && u.id && authCfg.accounts) {
              synced.current = false;
              // A failed read used to mark the lists synced anyway, and the
              // sync effect then saved this browser's pre-login lists over the
              // account's saved ones. Retry instead; write nothing until a read
              // has succeeded.
              const hydrate = (attempt: number) => {
                fetchState().then(st => {
                  setWatchlist(st.watch);
                  setAlerts(st.alerts);
                  if (st.onboarded) localStorage.setItem('mm_onboarded', '1');
                  synced.current = true;
                }).catch(() => {
                  if (attempt < 5) setTimeout(() => hydrate(attempt + 1), [2000, 5000, 15000, 30000, 60000][attempt]);
                });
              };
              hydrate(0);
            }
            if (page === 'landing') nav('dashboard');
            if (isSignup || localStorage.getItem('mm_onboarded') !== '1') setShowOnboard(true);
          }} />
      )}
      {showUpgrade && user && (
        <UpgradeModal user={user} setUser={setUser} initialBill={upgradeBill} stripeReady={authCfg.stripe} onClose={() => setShowUpgrade(false)} />
      )}
      {showOnboard && (
        <OnboardingModal user={user} setUser={setUser} onClose={() => setShowOnboard(false)} onNav={nav} />
      )}
      {payOk && (
        <div className="mm-modal-bg" onClick={() => setPayOk(false)}>
          <div className="mm-modal glass" onClick={e => e.stopPropagation()}>
            <button className="mm-modal-x" onClick={() => setPayOk(false)}>✕</button>
            <div className="mm-modal-orb"></div>
            <div className="mm-upgrade-done">
              <span className="mm-upgrade-check">✓</span>
              <h2>{tr('ชำระเงินสำเร็จ! 🎉', 'Payment successful! 🎉')}</h2>
              <p className="dim">{tr('ปลดล็อกทุกฟีเจอร์เรียบร้อย — ขอบคุณที่สนับสนุน Master of the Market 🐯', 'All features unlocked — thanks for supporting Master of the Market 🐯')}</p>
            </div>
          </div>
        </div>
      )}
      <TweaksPanel>
        <TweakSection label="Effects" />
        <TweakToggle label={tr('ราคาขยับสด', 'Live price motion')} value={t.livePrices} onChange={(v: boolean) => setTweak('livePrices', v)} />
        <TweakSlider label={tr('ความเข้มของออโรร่า', 'Aurora intensity')} value={t.fxIntensity} min={0} max={1.6} step={0.1}
          onChange={(v: number) => setTweak('fxIntensity', v)} />
        <TweakToggle label="Glassmorphism" value={t.glass} onChange={(v: boolean) => setTweak('glass', v)} />
        <TweakSection label="Theme" />
        <TweakColor label={tr('ชุดสีตลาด', 'Market color set')} value={t.accent}
          options={[['#00e0b0', '#ff3b5c', '#8b5cf6'], ['#22c55e', '#ef4444', '#a855f7'], ['#37c8f0', '#f0735a', '#c084fc'], ['#f0c75a', '#ff3b5c', '#00e0b0']]}
          onChange={(v: string[]) => setTweak('accent', v)} />
        <TweakSlider label={tr('ขนาดตัวอักษร', 'Font size')} value={t.fontScale} min={0.9} max={1.15} step={0.05} unit="×"
          onChange={(v: number) => setTweak('fontScale', v)} />
        <TweakToggle label={tr('หัวข้อ Serif แบบโลโก้', 'Serif headings (logo style)')} value={t.serifHeads} onChange={(v: boolean) => setTweak('serifHeads', v)} />
      </TweaksPanel>
    </div>
  );
}
